import React from 'react';
import { TableHead, TableRow, TableSortLabel, Box, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { StyledTableCell } from './StyledTableComponents';
import { IconByString } from '../../components/IconByString';

export type Order = 'asc' | 'desc';

export interface HeadCell {
  id: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center' | 'justify' | 'inherit';
  icon?: string;
}

interface GenericTableHeadProps {
  headCells: HeadCell[];
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string;
}

export const GenericTableHead = (props: GenericTableHeadProps) => {
  const { headCells, order, orderBy, onRequestSort } = props;
  const theme = useTheme();

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.common.white }}
    >
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable !== false ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                IconComponent={UnfoldMoreIcon}
                sx={{
                  fontWeight: 700,
                  color: `${theme.palette.common.white} !important`,
                  '&.Mui-active': {
                    color: `${theme.palette.common.white} !important`,
                  },
                  '& .MuiTableSortLabel-icon': {
                    opacity: orderBy === headCell.id ? 1 : 0.3,
                    color: `${theme.palette.common.white} !important`,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {headCell.icon && <IconByString iconName={headCell.icon} sx={{ fontSize: 18 }} />}
                  {headCell.label}
                </Box>
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {headCell.icon && <IconByString iconName={headCell.icon} sx={{ fontSize: 18 }} />}
                {headCell.label}
              </Box>
            )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
